// File-workflows admin page.
//
// Lists every workflow with its trigger, filename pattern, action count,
// run count, and enabled flag. Create / edit dialog drives the discriminated
// action union via per-action-type editors. Built-in workflows are
// half-locked: operators can flip enabled + priority but the actions array
// is read-only (the seed re-creates them anyway).

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FileWorkflow,
  FileWorkflowAction,
  FileWorkflowCondition,
  StorageContainer,
  Tag,
} from '@captain-tasko/shared';
import { FileWorkflowConditionSchema } from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { NestFilterBanner } from '../components/NestFilterBanner.js';
import { toast } from '../components/Toast.js';
import { SortableTh } from '../components/SortableTh.js';
import { useTableSort } from '../lib/sort.js';
import { RowActionsMenu } from '../components/RowActions.js';
import { useListSearch } from '../lib/use-list-search.js';
import { ListSearchInput } from '../components/ListSearchInput.js';
import { EditedTimestampStrip } from '../components/EditedTimestampStrip.js';

const ACTION_TYPES: { value: FileWorkflowAction['type']; label: string; help: string }[] = [
  { value: 'assign_tag',       label: 'Assign tag',       help: 'Tag the document. By id or by name (auto-creates if missing).' },
  { value: 'assign_container', label: 'Assign container', help: 'Add to a storage container by id or slug.' },
  { value: 'set_frontmatter',  label: 'Set frontmatter',  help: 'Write key/value pairs into the sidecar frontmatter. Values support {{tokens}}.' },
  { value: 'rename_file',      label: 'Rename file',      help: 'Set the document title from a template. {{originalName}}, {{date}}, {{uuid}}, {{frontmatter:key}}.' },
  { value: 'check_links',      label: 'Check links',      help: 'Scan content for markdown/bare URLs; write the list to the sidecar. Read-only — never modifies the file.' },
  { value: 'link_to_parent',   label: 'Link to parent',   help: 'Treat as a sidecar: find a parent doc by filename minus the configured suffix and stamp the link on the sidecar.' },
  { value: 'project_to_custom_properties', label: 'Project CP from frontmatter', help: '0.1.17+. Reads sidecar.frontmatter for every CustomProperty with a source pointer and writes the projected value into documents.customProperties. No parameters — the per-CP source path drives the projection. Idempotent.' },
];

// ─── CTSK-443 — conditions[] display + editor vocabulary ─────────────────
// Mirrors FileWorkflowConditionFieldSchema / FileWorkflowConditionOperatorSchema
// in packages/shared/src/schemas/file-workflow.ts. Validation itself goes
// through the shared zod schema — these lists only drive the UI.

const CONDITION_FIELDS: { value: FileWorkflowCondition['field']; label: string }[] = [
  { value: 'tag',             label: 'Tag' },
  { value: 'metadata_field',  label: 'Metadata field' },
  { value: 'custom_property', label: 'Custom property' },
  { value: 'file_type',       label: 'File type' },
  { value: 'filename',        label: 'Filename' },
  { value: 'container',       label: 'Container' },
];

const CONDITION_OPERATORS: FileWorkflowCondition['operator'][] = [
  'contains', 'not_contains',
  'equals', 'not_equals',
  'starts_with', 'not_starts_with',
  'ends_with', 'not_ends_with',
  'regex_match', 'not_regex_match',
  'is_empty', 'is_not_empty',
  'before', 'after',
];

/** Operators that take no value (value input hidden, value stripped on save). */
const NO_VALUE_OPERATORS = new Set<FileWorkflowCondition['operator']>(['is_empty', 'is_not_empty']);
/** Fields that require a fieldKey (frontmatter key / CP key name). */
const KEYED_FIELDS = new Set<FileWorkflowCondition['field']>(['metadata_field', 'custom_property']);

/** Positive-verb phrase per operator; negated pairs get an "except" prefix. */
const OPERATOR_PHRASES: Record<FileWorkflowCondition['operator'], string> = {
  contains: 'contains',        not_contains: 'contains',
  equals: 'equals',            not_equals: 'equals',
  starts_with: 'starts with',  not_starts_with: 'starts with',
  ends_with: 'ends with',      not_ends_with: 'ends with',
  regex_match: 'matches',      not_regex_match: 'matches',
  is_empty: 'is empty',        is_not_empty: 'is not empty',
  before: 'is before',         after: 'is after',
};

/** Plain-language one-liner: "except tag starts with source/", "if metadata_field:published equals true". */
function describeCondition(c: FileWorkflowCondition): string {
  const prefix = c.operator.startsWith('not_') ? 'except' : 'if';
  const fieldLabel = c.fieldKey ? `${c.field}:${c.fieldKey}` : c.field;
  const value = NO_VALUE_OPERATORS.has(c.operator) ? '' : ` ${c.value ?? ''}`;
  return `${prefix} ${fieldLabel} ${OPERATOR_PHRASES[c.operator]}${value}`.trimEnd();
}

/**
 * Drop stale optional fields before validate/submit: a fieldKey left over
 * from a keyed field, or a value left over from a valued operator. Keeps
 * the payload exactly what the shared schema expects.
 */
function normalizeCondition(c: FileWorkflowCondition): FileWorkflowCondition {
  const next: FileWorkflowCondition = { field: c.field, operator: c.operator };
  if (KEYED_FIELDS.has(c.field) && c.fieldKey) next.fieldKey = c.fieldKey;
  if (!NO_VALUE_OPERATORS.has(c.operator) && c.value !== undefined) next.value = c.value;
  return next;
}

/** Muted chip, same vocabulary as the built-in badge. */
function ConditionChip({ text, title }: { text: string; title?: string }) {
  return (
    <span
      title={title ?? text}
      style={{
        display: 'inline-block',
        fontSize: 11,
        padding: '2px 8px',
        borderRadius: 8,
        background: 'var(--surface-sunken)',
        color: 'var(--text-muted)',
        whiteSpace: 'nowrap',
        maxWidth: 220,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        verticalAlign: 'middle',
      }}
    >
      {text}
    </span>
  );
}

export function FileWorkflowsAdmin() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['file-workflows'],
    queryFn: () => apiList<FileWorkflow>('/file-workflows'),
  });
  const tagsQ = useQuery({ queryKey: ['tags'], queryFn: () => apiList<Tag>('/tags') });
  const containersQ = useQuery({ queryKey: ['storage-containers'], queryFn: () => apiList<StorageContainer>('/storage-containers') });

  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; workflow: FileWorkflow } | null>(null);

  // ?nest=<slug> deep-link from the sidebar File-wfs stat. Same v1
  // detection signal the bundle export uses: keep workflows whose
  // actions[] contains an assign_tag whose tag.name matches the
  // master tag `nest/<slug>`. action.tag carries the NAME (not id)
  // so this is a direct string compare.
  const [nestSlug, setNestSlug] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('nest');
  });

  const nestFilteredWorkflows = useMemo(() => {
    const all = list.data?.data ?? [];
    if (!nestSlug) return all;
    const masterName = `nest/${nestSlug}`;
    return all.filter((w) =>
      w.actions.some((a) => {
        if (a.type !== 'assign_tag') return false;
        // assign_tag.tag is a discriminated union { id } | { name };
        // only the name variant lets us match without a tags lookup.
        // Coverage is fine in practice — operator-authored workflows
        // for nests always reference the master tag by name.
        return 'name' in a.tag && a.tag.name === masterName;
      }),
    );
  }, [list.data?.data, nestSlug]);

  // FR #185 — top-of-list search on name + description (+ filename
  // pattern, which is the most-typed look-up key in practice).
  const search = useListSearch(nestFilteredWorkflows, {
    fields: [
      (w) => w.name,
      (w) => w.description ?? '',
      (w) => w.filenamePattern ?? '',
    ],
    urlParam: 'q',
  });

  const { sorted, sort, setSort } = useTableSort(search.filtered, {
    priority: (w: FileWorkflow) => w.priority,
    name: (w) => w.name,
    trigger: (w) => w.trigger,
    actions: (w) => w.actions.length,
    runCount: (w) => w.runCount,
    enabled: (w) => w.enabled,
  }, { key: 'priority', dir: 'desc' });

  const del = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/file-workflows/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Workflow deleted'); qc.invalidateQueries({ queryKey: ['file-workflows'] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const togglePatch = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiSingle<FileWorkflow>(`/file-workflows/${id}`, { method: 'PATCH', body: { enabled } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['file-workflows'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">File workflows</h1>
          <div className="page-subtitle">
            {nestSlug
              ? `${nestFilteredWorkflows.length} workflow${nestFilteredWorkflows.length === 1 ? '' : 's'} in this nest · higher priority runs first`
              : `${list.data?.data.length ?? 0} workflow${list.data?.data.length === 1 ? '' : 's'} · higher priority runs first`}
          </div>
        </div>
        <button className="btn btn--create" onClick={() => setDialog({ mode: 'create' })}>+ New workflow</button>
      </div>

      {nestSlug && (
        <NestFilterBanner
          slug={nestSlug}
          onClear={() => {
            setNestSlug(null);
            if (typeof window !== 'undefined') {
              const sp = new URLSearchParams(window.location.search);
              sp.delete('nest');
              const qs = sp.toString();
              const next = window.location.pathname + (qs ? `?${qs}` : '');
              window.history.replaceState(null, '', next);
            }
          }}
        />
      )}

      {list.data && list.data.data.length > 0 && (
        <ListSearchInput
          value={search.query}
          onChange={search.setQuery}
          placeholder="Search workflows by name, description, or pattern…"
          totalCount={nestFilteredWorkflows.length}
          filteredCount={search.filtered.length}
        />
      )}

      {list.isLoading && <SkeletonRows rows={4} />}
      {list.data && list.data.data.length === 0 && (
        <EmptyState
          title="No file workflows"
          body="Workflows run when a document is uploaded. They can tag it, drop it in a container, stamp frontmatter, and more."
          action={<button className="btn btn--create" onClick={() => setDialog({ mode: 'create' })}>+ New workflow</button>}
        />
      )}
      {list.data && list.data.data.length > 0 && search.isActive && search.filtered.length === 0 && (
        <EmptyState
          title="No workflows match your search"
          body={`Nothing matches "${search.query}". Try a shorter query or clear the search.`}
          action={<button className="btn" onClick={() => search.setQuery('')}>Clear search</button>}
        />
      )}
      {list.data && list.data.data.length > 0 && search.filtered.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <SortableTh sortKey="priority" sort={sort} onSortChange={setSort}>Priority</SortableTh>
              <SortableTh sortKey="name" sort={sort} onSortChange={setSort}>Name</SortableTh>
              <SortableTh sortKey="trigger" sort={sort} onSortChange={setSort}>Trigger</SortableTh>
              <th>Filename pattern</th>
              <th>Conditions</th>
              <SortableTh sortKey="actions" sort={sort} onSortChange={setSort}>Actions</SortableTh>
              <SortableTh sortKey="runCount" sort={sort} onSortChange={setSort}>Runs</SortableTh>
              <SortableTh sortKey="enabled" sort={sort} onSortChange={setSort}>Status</SortableTh>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((w) => (
              <tr key={w.id}>
                <td>{w.priority}</td>
                <td>
                  <strong>{w.name}</strong>
                  {w.isBuiltIn && <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', borderRadius: 8, background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}>built-in</span>}
                  {w.description && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{w.description}</div>}
                  {w.lastRunAt && <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Last run: {new Date(w.lastRunAt).toLocaleString()}</div>}
                </td>
                <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{w.trigger}</code></td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: w.filenamePattern ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                  {w.filenamePattern ?? '(any file)'}
                </td>
                <td>
                  {!w.conditions || w.conditions.length === 0
                    ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                    : w.conditions.length === 1
                      ? <ConditionChip text={describeCondition(w.conditions[0]!)} />
                      : (
                        <ConditionChip
                          text={`${w.conditions.length} conditions [${(w.matchLogic ?? 'all').toUpperCase()}]`}
                          title={w.conditions.map(describeCondition).join('\n')}
                        />
                      )}
                </td>
                <td>{w.actions.length}</td>
                <td>{w.runCount}</td>
                <td>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={w.enabled}
                      onChange={(e) => togglePatch.mutate({ id: w.id, enabled: e.target.checked })}
                    />
                    <span style={{ color: w.enabled ? 'var(--success-primary)' : 'var(--text-muted)', fontSize: 12 }}>
                      {w.enabled ? 'enabled' : 'disabled'}
                    </span>
                  </label>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <RowActionsMenu
                    actions={[
                      { label: 'Edit…', onClick: () => setDialog({ mode: 'edit', workflow: w }) },
                      { label: '', onClick: () => undefined, separator: true },
                      {
                        label: w.isBuiltIn ? 'Delete (built-in — disable instead)' : 'Delete',
                        destructive: true,
                        onClick: () => {
                          if (w.isBuiltIn) {
                            toast.error('Built-in workflows can’t be deleted. Disable them via the checkbox.');
                            return;
                          }
                          if (confirm(`Delete workflow "${w.name}"?`)) del.mutate(w.id);
                        },
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {dialog && (
        <WorkflowDialog
          workflow={dialog.mode === 'edit' ? dialog.workflow : null}
          tags={tagsQ.data?.data ?? []}
          containers={containersQ.data?.data ?? []}
          onCancel={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            qc.invalidateQueries({ queryKey: ['file-workflows'] });
          }}
        />
      )}
    </>
  );
}

function WorkflowDialog({
  workflow,
  tags,
  containers,
  onCancel,
  onSaved,
}: {
  workflow: FileWorkflow | null;
  tags: Tag[];
  containers: StorageContainer[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isEdit = workflow !== null;
  const isBuiltIn = workflow?.isBuiltIn ?? false;

  const [name, setName] = useState(workflow?.name ?? '');
  const [description, setDescription] = useState(workflow?.description ?? '');
  const [priority, setPriority] = useState(workflow?.priority ?? 10);
  const [enabled, setEnabled] = useState(workflow?.enabled ?? true);
  const [filenamePattern, setFilenamePattern] = useState(workflow?.filenamePattern ?? '');
  const [actions, setActions] = useState<FileWorkflowAction[]>(workflow?.actions ?? []);
  // CTSK-443 — matchLogic + conditions[] (both optional server-side; empty
  // array / undefined = match everything post-filenamePattern).
  const [matchLogic, setMatchLogic] = useState<'all' | 'any'>(workflow?.matchLogic ?? 'all');
  const [conditions, setConditions] = useState<FileWorkflowCondition[]>(workflow?.conditions ?? []);
  const [conditionErrors, setConditionErrors] = useState<Record<number, string>>({});
  const [testFilename, setTestFilename] = useState('');
  const [testResult, setTestResult] = useState<{ matches: boolean; actionCount: number } | null>(null);

  function setAction(i: number, next: FileWorkflowAction) {
    setActions((arr) => arr.map((a, idx) => (idx === i ? next : a)));
  }
  function addAction(type: FileWorkflowAction['type']) {
    let next: FileWorkflowAction;
    switch (type) {
      case 'assign_tag':       next = { type, tag: { name: '' } }; break;
      case 'assign_container': next = { type, container: { slug: '' } }; break;
      case 'set_frontmatter':  next = { type, fields: { key: 'value' } }; break;
      case 'rename_file':      next = { type, template: '{{originalName}}' }; break;
      case 'check_links':      next = { type }; break;
      case 'link_to_parent':   next = { type, suffix: '.meta.md' }; break;
    }
    setActions((arr) => [...arr, next]);
  }
  function removeAction(i: number) {
    setActions((arr) => arr.filter((_, idx) => idx !== i));
  }
  function moveAction(i: number, dir: -1 | 1) {
    setActions((arr) => {
      const next = [...arr];
      const j = i + dir;
      if (j < 0 || j >= next.length) return arr;
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  }

  // ── CTSK-443 — condition row handlers ──────────────────────────────────
  function setCondition(i: number, next: FileWorkflowCondition) {
    setConditions((arr) => arr.map((c, idx) => (idx === i ? next : c)));
    // Editing a row clears its stale validation error; re-validated on save.
    setConditionErrors((errs) => {
      if (!(i in errs)) return errs;
      const { [i]: _drop, ...rest } = errs;
      return rest;
    });
  }
  function addCondition() {
    setConditions((arr) => [...arr, { field: 'tag', operator: 'equals', value: '' }]);
  }
  function removeCondition(i: number) {
    setConditions((arr) => arr.filter((_, idx) => idx !== i));
    setConditionErrors({});
  }

  /**
   * Client-side gate mirroring the server: every row must pass the shared
   * FileWorkflowConditionSchema (fieldKey required for keyed fields, value
   * required for valued operators). Empty conditions array is always valid.
   */
  function validateConditions(): boolean {
    const errs: Record<number, string> = {};
    conditions.forEach((c, i) => {
      const parsed = FileWorkflowConditionSchema.safeParse(normalizeCondition(c));
      if (!parsed.success) {
        errs[i] = parsed.error.issues.map((issue) => issue.message).join('; ');
      }
    });
    setConditionErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description: description || null,
        priority,
        enabled,
        trigger: 'on_upload' as const,
        filenamePattern: filenamePattern.trim() || null,
        matchLogic,
        conditions: conditions.map(normalizeCondition),
        actions,
      };
      // For built-in workflows the API only accepts enabled+priority; send
      // just those fields rather than the whole body to avoid the 403.
      if (isBuiltIn && workflow) {
        return apiSingle<FileWorkflow>(`/file-workflows/${workflow.id}`, {
          method: 'PATCH',
          body: { enabled, priority },
        });
      }
      return workflow
        ? apiSingle<FileWorkflow>(`/file-workflows/${workflow.id}`, { method: 'PATCH', body })
        : apiSingle<FileWorkflow>('/file-workflows', { method: 'POST', body });
    },
    onSuccess: () => { toast.success(isEdit ? 'Workflow saved' : 'Workflow created'); onSaved(); },
    onError: (e: Error) => toast.error(e.message),
  });

  async function runTest() {
    if (!workflow || !testFilename.trim()) {
      // For unsaved workflows, evaluate the pattern locally — actions don't
      // matter for pattern testing.
      try {
        const re = filenamePattern.trim() ? new RegExp(filenamePattern.trim()) : null;
        const matches = re ? re.test(testFilename) : true;
        setTestResult({ matches, actionCount: matches ? actions.length : 0 });
      } catch {
        setTestResult({ matches: false, actionCount: 0 });
      }
      return;
    }
    try {
      const res = await apiSingle<{ matches: boolean; actions: FileWorkflowAction[] }>(
        `/file-workflows/${workflow.id}/test`,
        { method: 'POST', body: { filename: testFilename } },
      );
      setTestResult({ matches: res.matches, actionCount: res.actions.length });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Test failed');
    }
  }

  return (
    <div role="dialog" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.3)', display: 'grid', placeItems: 'center', zIndex: 2000 }} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface-raised)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', width: 760, maxHeight: '92vh', overflow: 'auto' }}>
        <h2 style={{ margin: 0, marginBottom: 12 }}>
          {isEdit ? `Edit workflow: ${workflow!.name}` : 'New file workflow'}
          {isBuiltIn && <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 6px', borderRadius: 8, background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}>built-in</span>}
        </h2>
        {workflow && <EditedTimestampStrip createdAt={workflow.createdAt} updatedAt={workflow.updatedAt} />}
        {isBuiltIn && (
          <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 0 }}>
            Built-in workflows can only change <strong>enabled</strong> and <strong>priority</strong>; everything else is restored from the seed on each boot.
          </p>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px', gap: 12 }}>
          <div className="field">
            <label htmlFor="w-name">Name</label>
            <input id="w-name" value={name} onChange={(e) => setName(e.target.value)} disabled={isBuiltIn} autoFocus />
          </div>
          <div className="field">
            <label htmlFor="w-pri">Priority</label>
            <input id="w-pri" type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
          </div>
          <div className="field">
            <label htmlFor="w-enabled" style={{ marginTop: 4 }}>Enabled</label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, paddingTop: 6 }}>
              <input id="w-enabled" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span style={{ fontSize: 12, color: enabled ? 'var(--success-primary)' : 'var(--text-muted)' }}>{enabled ? 'on' : 'off'}</span>
            </label>
          </div>
        </div>

        <div className="field">
          <label htmlFor="w-desc">Description</label>
          <input id="w-desc" value={description ?? ''} onChange={(e) => setDescription(e.target.value)} disabled={isBuiltIn} />
        </div>

        <div className="field">
          <label htmlFor="w-pat">Filename pattern (regex; blank = any file)</label>
          <input
            id="w-pat"
            value={filenamePattern ?? ''}
            onChange={(e) => setFilenamePattern(e.target.value)}
            placeholder={String.raw`\.meta\.md$`}
            disabled={isBuiltIn}
            style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
          />
        </div>

        <div className="field">
          <label>Conditions (evaluated after the filename pattern; empty = always run)</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {conditions.length > 0 && (
              <div role="radiogroup" aria-label="Match logic" style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="w-matchlogic"
                    checked={matchLogic === 'all'}
                    onChange={() => setMatchLogic('all')}
                    disabled={isBuiltIn}
                  />
                  Match all (AND)
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="w-matchlogic"
                    checked={matchLogic === 'any'}
                    onChange={() => setMatchLogic('any')}
                    disabled={isBuiltIn}
                  />
                  Match any (OR)
                </label>
              </div>
            )}
            {conditions.length === 0 && (
              <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '2px 0' }}>
                No conditions — the workflow runs for every file matching the pattern.
              </div>
            )}
            {conditions.map((c, i) => (
              <ConditionRowEditor
                key={i}
                condition={c}
                index={i}
                disabled={isBuiltIn}
                error={conditionErrors[i]}
                onChange={(next) => setCondition(i, next)}
                onRemove={() => removeCondition(i)}
              />
            ))}
            {!isBuiltIn && (
              <div>
                <button className="btn" type="button" onClick={addCondition}>+ Add condition</button>
              </div>
            )}
          </div>
        </div>

        <div className="field">
          <label>Test against filename</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={testFilename}
              onChange={(e) => setTestFilename(e.target.value)}
              placeholder="example.md"
              style={{ flex: 1, fontFamily: 'var(--font-mono)' }}
            />
            <button className="btn" type="button" onClick={runTest} disabled={!testFilename.trim()}>Test</button>
          </div>
          {testResult && (
            <div style={{ fontSize: 12, marginTop: 6, color: testResult.matches ? 'var(--success-primary)' : 'var(--text-muted)' }}>
              {testResult.matches
                ? `Matches — would apply ${testResult.actionCount} action${testResult.actionCount === 1 ? '' : 's'}.`
                : 'Does not match — workflow would not run.'}
            </div>
          )}
        </div>

        <div className="field">
          <label>Actions (run in order)</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {actions.length === 0 && (
              <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '6px 0' }}>
                No actions yet. Add one below.
              </div>
            )}
            {actions.map((a, i) => (
              <ActionEditor
                key={i}
                action={a}
                index={i}
                total={actions.length}
                tags={tags}
                containers={containers}
                disabled={isBuiltIn}
                onChange={(next) => setAction(i, next)}
                onMove={(dir) => moveAction(i, dir)}
                onRemove={() => removeAction(i)}
              />
            ))}
            {!isBuiltIn && (
              <div>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      addAction(e.target.value as FileWorkflowAction['type']);
                      e.target.value = '';
                    }
                  }}
                  style={{ fontSize: 13 }}
                >
                  <option value="">+ Add action…</option>
                  {ACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button
            className="btn btn--primary"
            disabled={!name.trim() || (actions.length === 0 && !isBuiltIn) || save.isPending}
            onClick={() => {
              if (!validateConditions()) return;
              save.mutate();
            }}
          >
            {save.isPending ? 'Saving…' : (isEdit ? 'Save changes' : 'Create workflow')}
          </button>
        </div>
      </div>
    </div>
  );
}

// CTSK-443 — one row of the conditions[] editor. fieldKey input only for
// keyed fields (metadata_field / custom_property); value input hidden for
// is_empty / is_not_empty. Stale optional fields are stripped on change so
// switching field/operator never leaves an invalid leftover in state.
function ConditionRowEditor({
  condition,
  index,
  disabled,
  error,
  onChange,
  onRemove,
}: {
  condition: FileWorkflowCondition;
  index: number;
  disabled: boolean;
  error: string | undefined;
  onChange: (next: FileWorkflowCondition) => void;
  onRemove: () => void;
}) {
  const needsKey = KEYED_FIELDS.has(condition.field);
  const needsValue = !NO_VALUE_OPERATORS.has(condition.operator);
  return (
    <div style={{ border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', padding: 8, background: 'var(--surface-canvas)' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          aria-label={`Condition ${index + 1} field`}
          value={condition.field}
          disabled={disabled}
          onChange={(e) => {
            const field = e.target.value as FileWorkflowCondition['field'];
            const next: FileWorkflowCondition = { ...condition, field };
            if (!KEYED_FIELDS.has(field)) delete next.fieldKey;
            onChange(next);
          }}
          style={{ fontSize: 13 }}
        >
          {CONDITION_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        {needsKey && (
          <input
            aria-label={`Condition ${index + 1} field key`}
            value={condition.fieldKey ?? ''}
            onChange={(e) => onChange({ ...condition, fieldKey: e.target.value })}
            placeholder={condition.field === 'metadata_field' ? 'frontmatter key' : 'property key'}
            disabled={disabled}
            style={{ flex: '0 1 150px', fontFamily: 'var(--font-mono)', fontSize: 12 }}
          />
        )}
        <select
          aria-label={`Condition ${index + 1} operator`}
          value={condition.operator}
          disabled={disabled}
          onChange={(e) => {
            const operator = e.target.value as FileWorkflowCondition['operator'];
            const next: FileWorkflowCondition = { ...condition, operator };
            if (NO_VALUE_OPERATORS.has(operator)) delete next.value;
            else if (next.value === undefined) next.value = '';
            onChange(next);
          }}
          style={{ fontSize: 13 }}
        >
          {CONDITION_OPERATORS.map((op) => <option key={op} value={op}>{op.replaceAll('_', ' ')}</option>)}
        </select>
        {needsValue && (
          <input
            aria-label={`Condition ${index + 1} value`}
            value={condition.value ?? ''}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
            placeholder="value"
            disabled={disabled}
            style={{ flex: 1, minWidth: 120, fontFamily: 'var(--font-mono)', fontSize: 12 }}
          />
        )}
        <button
          className="btn btn--ghost"
          type="button"
          disabled={disabled}
          onClick={onRemove}
          title="Remove condition"
          style={{ color: 'var(--danger-primary)' }}
        >
          ×
        </button>
      </div>
      {error && (
        <div role="alert" style={{ color: 'var(--danger-primary)', fontSize: 11, marginTop: 4 }}>
          {error}
        </div>
      )}
    </div>
  );
}

function ActionEditor({
  action,
  index,
  total,
  tags,
  containers,
  disabled,
  onChange,
  onMove,
  onRemove,
}: {
  action: FileWorkflowAction;
  index: number;
  total: number;
  tags: Tag[];
  containers: StorageContainer[];
  disabled: boolean;
  onChange: (next: FileWorkflowAction) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const meta = ACTION_TYPES.find((t) => t.value === action.type);
  return (
    <div style={{ border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', padding: 10, background: 'var(--surface-canvas)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <strong style={{ fontSize: 13 }}>{index + 1}. {meta?.label ?? action.type}</strong>
        <span style={{ flex: 1 }} />
        <button className="btn btn--ghost" type="button" disabled={disabled || index === 0} onClick={() => onMove(-1)} title="Move up">↑</button>
        <button className="btn btn--ghost" type="button" disabled={disabled || index === total - 1} onClick={() => onMove(1)} title="Move down">↓</button>
        <button className="btn btn--ghost" type="button" disabled={disabled} onClick={onRemove} title="Remove" style={{ color: 'var(--danger-primary)' }}>×</button>
      </div>
      {meta && <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 6 }}>{meta.help}</div>}
      <ActionBody action={action} tags={tags} containers={containers} disabled={disabled} onChange={onChange} />
    </div>
  );
}

function ActionBody({
  action,
  tags,
  containers,
  disabled,
  onChange,
}: {
  action: FileWorkflowAction;
  tags: Tag[];
  containers: StorageContainer[];
  disabled: boolean;
  onChange: (next: FileWorkflowAction) => void;
}) {
  if (action.type === 'assign_tag') {
    const byName = 'name' in action.tag;
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <select
          value={byName ? 'name' : 'id'}
          disabled={disabled}
          onChange={(e) => onChange({ type: 'assign_tag', tag: e.target.value === 'id' ? { id: '' } : { name: '' } })}
        >
          <option value="name">By name (auto-create)</option>
          <option value="id">By id (existing)</option>
        </select>
        {byName
          ? <input
              value={('name' in action.tag ? action.tag.name : '') ?? ''}
              onChange={(e) => onChange({ type: 'assign_tag', tag: { name: e.target.value } })}
              placeholder="new"
              disabled={disabled}
              style={{ flex: 1 }}
            />
          : <select
              value={('id' in action.tag ? action.tag.id : '') ?? ''}
              onChange={(e) => onChange({ type: 'assign_tag', tag: { id: e.target.value } })}
              disabled={disabled}
              style={{ flex: 1 }}
            >
              <option value="">— select —</option>
              {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>}
      </div>
    );
  }
  if (action.type === 'assign_container') {
    const bySlug = 'slug' in action.container;
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <select
          value={bySlug ? 'slug' : 'id'}
          disabled={disabled}
          onChange={(e) => onChange({ type: 'assign_container', container: e.target.value === 'id' ? { id: '' } : { slug: '' } })}
        >
          <option value="slug">By slug</option>
          <option value="id">By id</option>
        </select>
        {bySlug
          ? <input
              value={('slug' in action.container ? action.container.slug : '') ?? ''}
              onChange={(e) => onChange({ type: 'assign_container', container: { slug: e.target.value } })}
              placeholder="inbox"
              disabled={disabled}
              style={{ flex: 1, fontFamily: 'var(--font-mono)' }}
            />
          : <select
              value={('id' in action.container ? action.container.id : '') ?? ''}
              onChange={(e) => onChange({ type: 'assign_container', container: { id: e.target.value } })}
              disabled={disabled}
              style={{ flex: 1 }}
            >
              <option value="">— select —</option>
              {containers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>}
      </div>
    );
  }
  if (action.type === 'set_frontmatter') {
    const entries = Object.entries(action.fields);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {entries.map(([k, v], i) => (
          <div key={i} style={{ display: 'flex', gap: 4 }}>
            <input
              value={k}
              onChange={(e) => {
                const newKey = e.target.value;
                const next: Record<string, string> = {};
                entries.forEach(([ek, ev], ei) => { next[ei === i ? newKey : ek] = ev; });
                onChange({ type: 'set_frontmatter', fields: next });
              }}
              placeholder="key"
              disabled={disabled}
              style={{ flex: '0 0 30%', fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
            <input
              value={v}
              onChange={(e) => onChange({ type: 'set_frontmatter', fields: { ...action.fields, [k]: e.target.value } })}
              placeholder="value or {{token}}"
              disabled={disabled}
              style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
            <button className="btn btn--ghost" type="button" disabled={disabled || entries.length === 1} onClick={() => {
              const next = { ...action.fields };
              delete next[k];
              onChange({ type: 'set_frontmatter', fields: next });
            }}>×</button>
          </div>
        ))}
        <button className="btn" type="button" disabled={disabled} onClick={() => {
          let i = 1;
          let key = `field_${i}`;
          while (key in action.fields) { i++; key = `field_${i}`; }
          onChange({ type: 'set_frontmatter', fields: { ...action.fields, [key]: '' } });
        }}>+ Add field</button>
      </div>
    );
  }
  if (action.type === 'rename_file') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <input
          value={action.template}
          onChange={(e) => onChange({ type: 'rename_file', template: e.target.value, alsoUpdateSlug: action.alsoUpdateSlug })}
          placeholder="{{date}}-{{originalName}}"
          disabled={disabled}
          style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
        />
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          <input
            type="checkbox"
            checked={!!action.alsoUpdateSlug}
            onChange={(e) => onChange({ type: 'rename_file', template: action.template, alsoUpdateSlug: e.target.checked })}
            disabled={disabled}
          /> Also update the URL slug
        </label>
      </div>
    );
  }
  if (action.type === 'check_links') {
    return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No configuration required.</div>;
  }
  if (action.type === 'link_to_parent') {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 12 }}>Suffix:</span>
        <input
          value={action.suffix ?? '.meta.md'}
          onChange={(e) => onChange({ type: 'link_to_parent', suffix: e.target.value })}
          disabled={disabled}
          style={{ width: 160, fontFamily: 'var(--font-mono)', fontSize: 13 }}
        />
      </div>
    );
  }
  if (action.type === 'project_to_custom_properties') {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        No configuration required — the projection is driven by each CustomProperty's <code style={{ fontFamily: 'var(--font-mono)' }}>source</code> pointer.
      </div>
    );
  }
  // Exhaustiveness
  return null;
}
